import { catchError, filter, lastValueFrom, of, take, timeout } from "rxjs";
import { Emitter } from "typed-rx-emitter";
import type { LangEnums } from "./storage-manager";
import Sdk from "@datarangers/sdk-javascript";
// Sample
// export interface NativeOutgoingMessage {
//   depositeSecuritiesClick: never;
//   test1: { p: number };
// }
// export interface NativeIncomingMessage {
//   test1Rsp: { p: number };
// }
// app方发送的消息格式为;
// window.__onChiefBridgeMessage(
//   JSON.stringify({
//     project: "stock-transfer-activity",
//     id: "cc1c4915-0d39-4110-8d70-a5a5d6fe5baf",
//     type: "test1Rsp",
//     payload: { p: 111 },
//   })
// );

let dataFinderAppId = 0;
// global.d.ts 中有 __MODE__ 的定义
if (__MODE__ === "prod") {
  dataFinderAppId = 20004063;
} else {
  dataFinderAppId = 20004064;
}

export interface DataFinderData {
  /**
   * 页面浏览
   */
  page_view: {
    /**
     * 页面名称，例如首页主页
     */
    page_name: string;
    /**
     * 场景，例如资产查询
     */
    scenes: string;
    /**
     * 页面ID，例如 sy
     */
    p_id: string;
    /**
     * 楼层ID，为空则填写 w
     */
    m_id?: (string & {}) | "w";
    /**
     * 模板ID，为空则填写 w
     */
    t_id?: (string & {}) | "w";
    /**
     * 资源位ID，为空则填写 w
     */
    s_id?: (string & {}) | "w";
    /**
     * 坑位ID，为空则填写 w
     */
    e_id?: (string & {}) | "w";
    /**
     * 运营位ID
     */
    placement_ID?: (string & {}) | "w";
    /**
     * H5活动标题
     */
    H5_title?: (string & {}) | "w";
    /**
     * 客户ID
     */
    CID?: (string & {}) | "w";
    /**
     * 时间
     */
    time?: (string & {}) | "w";
    /**
     * 当前 页面url
     */
    H5_url?: string | "w";
    /**
     * 渠道，从 URL query 参数 from_channel 获取
     */
    from_channel?: string;
  };
  /**
   * 元素点击
   */
  element_click: {
    /**
     * 页面名称，例如首页主页
     */
    page_name: string;
    /**
     * 元素名称，例如首页banner点击
     */
    element_name: string;

    /**
     * 元素名称，基金名称：中文简体
     */
    fund_name?: string;
    /**
     * 场景，例如资产查询
     */
    scenes: string;
    /**
     * 页面ID，例如 sy
     */
    p_id: string;
    /**
     * 楼层ID，为空则填写 w
     */
    m_id?: (string & {}) | "w";
    /**
     * 模板ID，为空则填写 w
     */
    t_id?: (string & {}) | "w";
    /**
     * 资源位ID，为空则填写 w
     */
    s_id?: (string & {}) | "w";
    /**
     * 坑位ID，为空则填写 w
     */
    e_id?: (string & {}) | "w";
    placement_ID?: (string & {}) | "w";
    H5_title?: (string & {}) | "w";
    CID?: (string & {}) | "w";
    time?: (string & {}) | "w";
    H5_time?: (string & {}) | "w";
    /**
     * 渠道，从 URL query 参数 from_channel 获取
     */
    from_channel?: string;
    /**
     * 邀请码，邀请好友相关点击事件上报
     */
    referral_code?: string;
  };

  /**
   * 元素曝光
   */
  element_view: {
    /**
     * 页面名称，例如首页主页
     */
    page_name: string;
    /**
     * 元素名称，例如首页banner点击
     */
    element_name: string;
    /**
     * 场景，例如资产查询
     */
    scenes: string;
    /**
     * 页面ID，例如 sy
     */
    p_id: string;
    /**
     * 楼层ID，为空则填写 w
     */
    m_id?: (string & {}) | "w";
    /**
     * 模板ID，为空则填写 w
     */
    t_id?: (string & {}) | "w";
    /**
     * 资源位ID，为空则填写 w
     */
    s_id?: (string & {}) | "w";
    /**
     * 坑位ID，为空则填写 w
     */
    e_id?: (string & {}) | "w";
  };
}
export interface SharedOutgoingMessage {
  /**
   * API 返回401
   */
  unauthorized: {
    /**
     * 当token 失效的时候 code还是-201  status会有两个情况 0 表示自动过期  1表示被挤下线,
     * 此时可以弹窗窗口说明情况后，跳转到登陆页面
     */
    code: "-201" | "-202";
    /**
     * 当token 未失效 但当前会话对应的api访问能力（例如下单那些）失效的时候， code=-202
     *此时可以弹窗窗口让用户输入密码验证
     */
    status: number;
  };

  /**
   * 显示 Toast 消息
   */
  showToast: {
    text: string;
    type?: "error" | "success" | "warning" | "none";
  };

  /**
   * 关闭窗口
   */
  toClose: never;

  /**
   * 火山埋点数据
   */
  sendDatafinder: {
    eventName: keyof DataFinderData;
    params: DataFinderData[keyof DataFinderData];
  };

  /**
   * 启动 App 原生 Loading
   */
  startAppLoading: never;

  /**
   * 停止 App 原生 Loading
   */
  stopAppLoading: never;

  /**
   * 通知 App 改变状态栏文字颜色
   */
  statusTextColor: {
    /** "0" = 白色, "1" = 黑色 */
    color: "0" | "1";
  };
}

export interface SharedIncomingMessage {
  toGoBack: object;
  changeTheme: {
    isDarkMode: "Y" | "N";
  };
  changeLanguage: {
    language: LangEnums;
  };
}

function uuidv4() {
  //throw new Error("Function not implemented.");
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// 只允许 string 类型的 key
type StringKeyOf<T> = Extract<keyof T, string>;
export type TypedIncomingMessage<IncomingMessage extends Record<string, any>> =
  {
    [K in StringKeyOf<IncomingMessage>]: { payload: IncomingMessage[K] } & {
      /**
       * 如果是响应消息，则为请求的 id
       */
      id: string;
      /**
       * 项目名，每个项目固定
       */
      project: string;
      /**
       * 消息类型，必须与IncomingMessage中的key一致
       */
      type: K;
    };
  };

export function isIos(): boolean {
  return (
    typeof window.webkit?.messageHandlers?.chiefBridge?.postMessage ===
      "function" ||
    typeof window.webkit?.messageHandlers?.phoneCall?.postMessage ===
      "function" ||
    typeof window.webkit?.messageHandlers?.openMarginAccount?.postMessage ===
      "function"
  );
}
export function isAndroid(): boolean {
  return (
    typeof window.android?.chiefBridge === "function" ||
    typeof window.android?.phoneCall === "function" ||
    typeof window.android?.openMarginAccount === "function"
  );
}
export function isEmbeddedMobile(): boolean {
  return isIos() || isAndroid();
}
export function isDesktopMac(): boolean {
  return typeof window.chiefDesktop?.mac === "function";
}

export function isDesktopWindows(): boolean {
  return typeof window.chiefDesktop?.windows === "function";
}
export function isEmbeddedDesktop(): boolean {
  return isDesktopMac() || isDesktopWindows();
}
export function isEmbedded(): boolean {
  return isEmbeddedMobile() || isEmbeddedDesktop();
}

/**
 * 版本阈值配置
 * 满足版本要求时使用 App 原生特性（NavBar、Loading、安全区域等）
 */
export const VERSION_THRESHOLDS = {
  chiefBao: {
    ios: "0.9.3",
    android: "0.9.5",
  },
  chief: {
    ios: "10.8.8",
    android: "10.8.7",
  },
} as const;

/**
 * 判断是否运行在致富证券 App (ChiefBao)
 */
export function isChiefBaoApp(): boolean {
  const channel = localStorage.getItem("channel") || "";
  return ["chiefbao_ios", "chiefbao_android"].includes(channel);
}

/**
 * 判断是否运行在致富通 App (Chief)
 */
export function isChiefApp(): boolean {
  const channel = localStorage.getItem("channel") || "";
  return ["chief_ios", "android", "chief_android"].includes(channel);
}

/**
 * 判断是否运行在已知的 App 中（致富证券或致富通）
 */
export function isKnownApp(): boolean {
  return isChiefBaoApp() || isChiefApp();
}

/**
 * 比较版本号，支持 "0.9.3(1)" 这种格式，只取主版本号部分比较
 * @returns true 如果 version > target
 */
function compareVersion(version: string, target: string): boolean {
  const cleanVersion = version.replace(/\(.*\)$/, "").trim();
  const vParts = cleanVersion.split(".").map((n) => parseInt(n, 10) || 0);
  const tParts = target.split(".").map((n) => parseInt(n, 10) || 0);

  for (let i = 0; i < Math.max(vParts.length, tParts.length); i++) {
    const v = vParts[i] || 0;
    const t = tParts[i] || 0;
    if (v > t) return true;
    if (v < t) return false;
  }
  return false;
}

/**
 * 获取当前 App 版本是否满足原生特性要求
 * 根据 App 类型和平台选择对应的版本阈值
 */
export function meetsVersionRequirement(): boolean {
  const iosVersion = localStorage.getItem("iosVersion") || "";
  const aosVersion = localStorage.getItem("aosVersion") || "";

  if (isChiefBaoApp()) {
    if (isIos() && iosVersion) {
      return compareVersion(iosVersion, VERSION_THRESHOLDS.chiefBao.ios);
    }
    if (isAndroid() && aosVersion) {
      return compareVersion(aosVersion, VERSION_THRESHOLDS.chiefBao.android);
    }
  }

  if (isChiefApp()) {
    if (isIos() && iosVersion) {
      return compareVersion(iosVersion, VERSION_THRESHOLDS.chief.ios);
    }
    if (isAndroid() && aosVersion) {
      return compareVersion(aosVersion, VERSION_THRESHOLDS.chief.android);
    }
  }

  return false;
}

/**
 * 判断是否使用 App 原生 Loading
 * 条件：运行在已知 App 中 且 版本满足要求
 */
export function shouldUseAppLoading(): boolean {
  if (!isEmbeddedMobile()) {
    return false;
  }
  if (!isKnownApp()) {
    return false;
  }
  return meetsVersionRequirement();
}

/**
 * 判断是否显示 H5 NavBar
 * 条件：
 * 1. 运行在 iOS 或 Android 环境
 * 2. 运行在已知的 App 中（致富证券或致富通）
 * 3. App 版本满足对应的阈值要求
 */
export function shouldShowNavBar(): boolean {
  if (!isEmbeddedMobile()) {
    return false;
  }
  return isKnownApp();
}

/**
 * 判断是否需要向上偏移（抵消 App 预留的导航条空间）
 *
 * 返回 true 的场景：
 * 1. 浏览器环境（非嵌入式 WebView）：App 预留了 88px 空间，需要偏移抵消
 * 2. 已知 App 但版本不满足阈值要求：旧版本 App 自带导航条，需要偏移抵消预留空间
 *
 * 返回 false 的场景：
 * 1. 未知 App（非致富证券/致富通）：不需要偏移
 * 2. 已知 App 且版本满足要求：新版本 App 不预留空间，由 H5 NavBar 管理
 */
export function shouldOffsetUp(): boolean {
  // 浏览器环境：App 预留了空间，需要偏移抵消
  if (!isEmbeddedMobile()) {
    return true;
  }
  // 未知 App 不需要偏移
  if (!isKnownApp()) {
    return false;
  }
  // 已知 App 但版本不满足要求时需要偏移
  return !meetsVersionRequirement();
}

/**
 * 直接关闭 App 原生 Loading（不依赖 ChiefBridge 实例）
 * 用于 storage-manager 等无法访问 bridge 实例的场景
 */
export function hideAppLoadingDirect(): void {
  if (!shouldUseAppLoading()) return;

  const message = JSON.stringify({
    id: "auto-hide-loading",
    project: "common",
    type: "stopAppLoading",
    payload: [],
  });

  if (
    typeof window.webkit?.messageHandlers?.chiefBridge?.postMessage ===
    "function"
  ) {
    window.webkit.messageHandlers.chiefBridge.postMessage(message);
  } else if (typeof window.android?.chiefBridge === "function") {
    window.android.chiefBridge(message);
  }
}

/**
 * 通知 App 改变状态栏文字颜色（不依赖 ChiefBridge 实例）
 * 条件：致富通 App + 版本满足阈值 + 文字颜色为纯白或纯黑
 * @param hexColor 当前文字颜色（hex 格式，如 "#ffffff"）
 */
export function notifyStatusTextColor(hexColor: string): void {
  // 仅致富通 App 且版本满足要求时发送
  if (!isChiefApp() || !meetsVersionRequirement()) return;

  const lower = hexColor.toLowerCase();
  // 仅纯白或纯黑时通知
  if (lower !== "#ffffff" && lower !== "#000000") return;

  // "0" = 白色, "1" = 黑色
  const color: "0" | "1" = lower === "#ffffff" ? "0" : "1";

  const message = JSON.stringify({
    id: "status-text-color",
    project: "common",
    type: "statusTextColor",
    payload: [{ color }],
  });

  if (
    typeof window.webkit?.messageHandlers?.chiefBridge?.postMessage ===
    "function"
  ) {
    window.webkit.messageHandlers.chiefBridge.postMessage(message);
  } else if (typeof window.android?.chiefBridge === "function") {
    window.android.chiefBridge(message);
  }
}

export class ChiefBridge<
  OutgoingMessage extends Record<string, any>,
  IncomingMessage extends Record<string, any> = {},
> {
  private _emitter = new Emitter<
    TypedIncomingMessage<IncomingMessage & SharedIncomingMessage>
  >();

  get incomingMessageEmitter(): Emitter<
    TypedIncomingMessage<IncomingMessage & SharedIncomingMessage>
  > {
    return this._emitter;
  }

  constructor(
    private readonly _projectName: string,
    private enableDataFinder = true,
  ) {
    window.__onChiefBridgeMessage = (message: string): void => {
      console.debug("Received message from native:", message);
      try {
        const data = JSON.parse(message);
        if (data.project !== this._projectName) {
          console.warn("Unknown project:", data.project);
          return;
        }
        if (!data.type) {
          console.warn("Message type is missing:", data);
          return;
        }
        this._emitter.emit(
          data.type as StringKeyOf<IncomingMessage & SharedIncomingMessage>,
          data,
        );
      } catch (error) {
        console.error("Error parsing message:", error);
      }
    };
    if (!isEmbedded() && this.enableDataFinder) {
      this._initDataFinder();
    }
  }

  private _initDataFinder(): void {
    Sdk.init({
      enable_debug: true,
      log: true,
      app_id: dataFinderAppId, //App Name: chieftrade_h5
      channel: "sg",
      channel_domain: "https://gator.uba.ap-southeast-1.volces.com",
    });
    Sdk.config({
      user_unique_id: localStorage.getItem("clientCode") || "",
      user_id: localStorage.getItem("clientCode") || "",
    });
    Sdk.start();
  }

  private _internalCallNative<
    MessageType extends StringKeyOf<OutgoingMessage & SharedOutgoingMessage>,
  >(
    type: MessageType,
    id: string,
    ...param: (OutgoingMessage &
      SharedOutgoingMessage)[MessageType] extends never
      ? []
      : [(OutgoingMessage & SharedOutgoingMessage)[MessageType]]
  ): void {
    const message = JSON.stringify({
      id: id,
      // 项目名，每个项目固定
      project: this._projectName,
      // 消息类型，必须与NativeMessage中的key一致
      type,
      // 消息参数，必须与NativeMessage中对应key的类型一致
      payload: param,
    });
    if (!isEmbedded()) {
      //alert(`此步骤通知 app 进行, type: ${type}\n message: ${message}`);
      console.log(`此步骤通知 app 进行, type: ${type}\n message: ${message}`);
      return;
    }
    if (
      typeof window.webkit?.messageHandlers?.chiefBridge?.postMessage ===
      "function"
    ) {
      window.webkit.messageHandlers.chiefBridge.postMessage(message);

      return;
    }
    if (typeof window.android?.chiefBridge === "function") {
      window.android.chiefBridge(message);
      return;
    }
    if (typeof window.chiefDesktop?.mac === "function") {
      window.chiefDesktop.mac(message);
      return;
    }
    if (typeof window.chiefDesktop?.windows === "function") {
      window.chiefDesktop.windows(message);
      return;
    }
  }
  callNative<
    MessageType extends StringKeyOf<OutgoingMessage & SharedOutgoingMessage>,
  >(
    type: MessageType,
    ...param: (OutgoingMessage &
      SharedOutgoingMessage)[MessageType] extends never
      ? []
      : [(OutgoingMessage & SharedOutgoingMessage)[MessageType]]
  ): void {
    this._internalCallNative(type, uuidv4(), ...param);
  }

  async callNativeWithResponse<
    MessageType extends StringKeyOf<OutgoingMessage>,
    ResponseType extends StringKeyOf<IncomingMessage & SharedIncomingMessage>,
  >(
    type: MessageType,
    responseType: ResponseType,
    rspTimeout: number = 5000,
    ...param: (OutgoingMessage &
      SharedOutgoingMessage)[MessageType] extends never
      ? []
      : [(OutgoingMessage & SharedOutgoingMessage)[MessageType]]
  ): Promise<
    | TypedIncomingMessage<
        IncomingMessage & SharedIncomingMessage
      >[ResponseType]
    | undefined
  > {
    const id = uuidv4();
    setTimeout(() => {
      this._internalCallNative(type, id, ...param);
    }, 0);
    const r = await lastValueFrom(
      this._emitter.on(responseType).pipe(
        filter((response) => {
          return response.id === id;
        }),
        take(1),
        timeout(rspTimeout),
        catchError((_error) => {
          return of(undefined);
        }),
      ),
    );
    return r;
  }

  async sendDatafinder<EvName extends keyof DataFinderData>(
    event: EvName,
    data: DataFinderData[EvName],
  ): Promise<void> {
    if (!this.enableDataFinder) {
      return;
    }
    data.m_id = data.m_id || "w";
    data.t_id = data.t_id || "w";
    data.s_id = data.s_id || "w";
    data.e_id = data.e_id || "w";
    (data as unknown as { uuid: string }).uuid =
      localStorage.getItem("userid") || "";
    if (isEmbedded()) {
      //@ts-ignore
      this.callNative("sendDatafinder", {
        eventName: event,
        params: data,
      });
      return;
    }
    try {
      Sdk.event(event, data);
    } catch (error) {
      console.error("DataFinder event error:", error);
    }
  }

  /**
   * 显示 Loading
   * @returns true 表示使用了 App 原生 Loading，调用方不需要显示 H5 Loading
   * @returns false 表示未使用 App Loading，调用方需要显示 H5 Loading
   */
  showLoading(): boolean {
    if (shouldUseAppLoading()) {
      this.callNative("startAppLoading");
      return true;
    }
    return false;
  }

  /**
   * 隐藏 Loading
   * @returns true 表示使用了 App 原生 Loading，调用方不需要隐藏 H5 Loading
   * @returns false 表示未使用 App Loading，调用方需要隐藏 H5 Loading
   */
  hideLoading(): boolean {
    if (shouldUseAppLoading()) {
      this.callNative("stopAppLoading");
      return true;
    }
    return false;
  }
}
